import { taskPriorityOptions, taskStatusOptions } from "../../helpers/utils";

/** Task statuses as the server numbers them; a task is active when it is anything but completed. */
export const COMPLETED_STATUS = 3;
export const ACTIVE_STATUSES = taskStatusOptions.filter((option) => option.value !== COMPLETED_STATUS).map((option) => option.value);
export const ALL_STATUSES = taskStatusOptions.map((option) => option.value);

export const STATUS_OPTIONS = taskStatusOptions;
export const PRIORITY_OPTIONS = taskPriorityOptions;

/**
 * What the assignments of a resource are filtered by. The flags (critical, overdue, milestone, open risks) are the
 * ones the counts of Resource Insights are made of, so a count can open the list it counts.
 */
export const DEFAULT_ASSIGNMENT_FILTERS = {
  projectId: null,
  clientId: null,
  statuses: ACTIVE_STATUSES,
  priorities: [],
  critical: false,
  overdue: false,
  milestone: false,
  openRisks: false,
  startDate: null,
  endDate: null,
  search: "",
};

/** The filters a count of Resource Insights stands for. */
const COUNT_FILTERS = {
  active: {},
  completed: { statuses: [COMPLETED_STATUS] },
  overdue: { overdue: true },
  critical: { critical: true },
  milestones: { milestone: true },
  openRisks: { openRisks: true },
};

/**
 * The filters Resource Insights starts from: active tasks in the period it is about, which its host chooses. The
 * user can change any of it.
 *
 * @param period  `{ startDate, endDate }` as yyyy-MM-dd
 */
export function defaultFiltersFor(period) {
  return { ...DEFAULT_ASSIGNMENT_FILTERS, startDate: period.startDate, endDate: period.endDate };
}

/** The filters that show the tasks behind one of the counts of Resource Insights, optionally within one project. */
export function filtersForCount(count, projectId, defaults) {
  return { ...defaults, ...COUNT_FILTERS[count], projectId: projectId ?? null };
}

/** The filters that show the tasks of one project in the period, whatever their status. */
export function filtersForProject(projectId, defaults) {
  return { ...defaults, statuses: ALL_STATUSES, projectId };
}

/** The filters that show the tasks of every project of one client in the period, whatever their status. */
export function filtersForClient(clientId, defaults) {
  return { ...defaults, statuses: ALL_STATUSES, clientId };
}

const sameSet = (a, b) => a.length === b.length && a.every((value) => b.includes(value));

/** The yes/no conditions a list of assignments can be limited to. */
// The colours of the pills of a task, which the filters wear too so a status, a priority or a flag looks the same
// wherever it appears: `solid` is the colour of the pill (white text on it), `soft`, `line` and `text` its tint,
// used until the choice is picked.
const GREEN = { solid: "#5aa55a", soft: "#eef6ee", line: "#c6e0c6", text: "#3d7a3d" };
const AMBER = { solid: "#edb126", soft: "#fdf5e0", line: "#f6dc9a", text: "#8a6200" };
const RED = { solid: "#d1424f", soft: "#fcecee", line: "#f1c3c8", text: "#b3303d" };
const BLUE = { solid: "#0f6cbd", soft: "#e8f1fa", line: "#b9d6ee", text: "#0f6cbd" };
const GREY = { solid: "#aaaaaa", soft: "#f4f4f4", line: "#dcdcdc", text: "#666666" };
const PURPLE = { solid: "#342c82", soft: "#ece7fd", line: "#d8d0f0", text: "#342c82" };

/** The colour of each status of a task, as its pill in the list shows it. */
export const STATUS_TONES = { 0: GREEN, 1: AMBER, 2: RED, 3: BLUE, 4: GREY };

/** The colour of each priority of a task, as its pill in the list shows it. */
export const PRIORITY_TONES = { 0: GREEN, 1: AMBER, 2: RED };

/** The colour of each flag, as the pill of the flag in the list shows it; milestones take the primary colour. */
export const FLAG_TONES = { critical: RED, overdue: RED, milestone: PURPLE, openRisks: AMBER };

export const FLAG_OPTIONS = [
  { key: "critical", label: "Critical" },
  { key: "overdue", label: "Overdue" },
  { key: "milestone", label: "Milestones" },
  { key: "openRisks", label: "Open risks" },
];

/**
 * What is applied beyond the defaults, as pills: `{ key, label, tone, remove(filters) }`, where `remove` gives the
 * filters without that one condition and `tone` is the colour of a status, a priority or a flag (none for the rest). Active tasks are what the list shows to begin with, so that alone is no pill.
 *
 * @param projects  the projects Resource Insights knows, to name the project and client filters
 * @param formatDate  writes a day (yyyy-MM-dd) the way the user's settings do
 */
export function appliedFilters(filters, projects, formatDate) {
  const pills = [];

  if (filters.projectId) {
    pills.push({ key: "project", label: projects.find((project) => project.id === filters.projectId)?.name ?? "Project", remove: (current) => ({ ...current, projectId: null }) });
  }

  if (filters.clientId) {
    pills.push({ key: "client", label: projects.find((project) => project.client?.id === filters.clientId)?.client?.name ?? "Client", remove: (current) => ({ ...current, clientId: null }) });
  }

  if (!sameSet(filters.statuses, ACTIVE_STATUSES)) {
    if (filters.statuses.length === 0 || sameSet(filters.statuses, ALL_STATUSES)) {
      pills.push({ key: "status-all", label: "All statuses", remove: (current) => ({ ...current, statuses: ACTIVE_STATUSES }) });
    } else {
      filters.statuses.forEach((status) => {
        pills.push({
          key: `status-${status}`,
          label: STATUS_OPTIONS.find((option) => option.value === status)?.name ?? "Status",
          tone: STATUS_TONES[status],
          remove: (current) => ({ ...current, statuses: current.statuses.filter((value) => value !== status) }),
        });
      });
    }
  }

  filters.priorities.forEach((priority) => {
    pills.push({
      key: `priority-${priority}`,
      label: `${PRIORITY_OPTIONS.find((option) => option.value === priority)?.name ?? "Priority"} priority`,
      tone: PRIORITY_TONES[priority],
      remove: (current) => ({ ...current, priorities: current.priorities.filter((value) => value !== priority) }),
    });
  });

  FLAG_OPTIONS.forEach(({ key, label }) => {
    if (filters[key]) pills.push({ key, label, tone: FLAG_TONES[key], remove: (current) => ({ ...current, [key]: false }) });
  });

  if (filters.startDate) pills.push({ key: "from", label: `From ${formatDate(filters.startDate)}`, remove: (current) => ({ ...current, startDate: null }) });
  if (filters.endDate) pills.push({ key: "until", label: `Until ${formatDate(filters.endDate)}`, remove: (current) => ({ ...current, endDate: null }) });

  return pills;
}

/** How many conditions beyond the defaults are applied. */
export const countAppliedFilters = (filters) => appliedFilters(filters, [], String).length;

/** Whether anything but what Resource Insights starts from (`defaults`) is asked for, which is what Reset puts back. */
export function hasCustomFilters(filters, defaults) {
  return (
    Boolean(filters.projectId) ||
    Boolean(filters.clientId) ||
    !sameSet(filters.statuses, defaults.statuses) ||
    filters.priorities.length > 0 ||
    filters.critical ||
    filters.overdue ||
    filters.milestone ||
    filters.openRisks ||
    filters.startDate !== defaults.startDate ||
    filters.endDate !== defaults.endDate ||
    filters.search.trim() !== ""
  );
}

/** The query of the assignments request for some filters; a project Resource Insights is scoped to always applies. */
export function assignmentQuery(filters, scopeProjectId) {
  const statuses = sameSet(filters.statuses, ALL_STATUSES) ? [] : filters.statuses;

  return {
    projectId: filters.projectId || scopeProjectId || undefined,
    clientId: filters.clientId || undefined,
    statuses,
    priorities: filters.priorities,
    isCritical: filters.critical ? true : undefined,
    isMilestone: filters.milestone ? true : undefined,
    isOverdue: filters.overdue ? true : undefined,
    hasOpenRisks: filters.openRisks ? true : undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    searchTerm: filters.search.trim() || undefined,
  };
}
